import { ReactNode, useCallback, useMemo } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { getIn } from 'final-form';
import { FormSpy } from 'react-final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmFormRef,
  getKnmStatus,
  getKnmSystemType,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import { HadToBeHowInSignMessage } from '../../MainModule/components/HadToBeHowInSignMessage';
import styles from './ErknmInspectors.module.scss';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { IKnmStatus } from '../../../APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { ErvkCorrectionRequiredInfo } from '../../MainModule/components/ErvkCorrectionRequiredInfo';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { useShowMore } from '../../../hooks/useShowMore';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';

interface IErknmInspectorsProps {
  watchOnly: boolean;
  name: string;
  title: ReactNode;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  blockId: string;
  isDisableDelete?: boolean;
  getItemBlockId: (id: string) => string;
}

interface IErknmInspectorsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmSystemType: KnmSystemTypesEnum | undefined;
  status: IKnmStatus | undefined;
  settings: IAdminSetting[];
  created: string | null | undefined;
  isWatchOnlyByTestUser: boolean;
}

const ErknmInspectorsWithoutStore = ({
  watchOnly,
  settings,
  name,
  title,
  status,
  positionsSelectOptions,
  blockId,
  isDisableDelete,
  getItemBlockId,
  formRef,
  created,
  knmSystemType,
  isWatchOnlyByTestUser,
}: IErknmInspectorsProps & IErknmInspectorsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const pmType = formRef?.current.getFieldState('pmType')?.value;

  const isValid4121Cert = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_CERT_STARTS_FROM,
  );

  const addInspector = useCallback(() => {
    if (!watchOnly && push) {
      const addingInspector: IKnmErknmFormInspector = {
        fullName: undefined,
        position: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
      };

      push(name, addingInspector);
    }
  }, [name, push, watchOnly]);

  const makeRemoveInspectorHandler = (index: number) => () => !watchOnly && remove && remove(name, index);

  /**
   * Определение, надо ли показывать подсказку для PM
   */
  const shouldShowAdviceFromPm = useMemo(() => {
    return (
      knmSystemType === KnmSystemTypesEnum.PM &&
      pmType?.code === ProfKindCodesEnum.OP &&
      (!status ||
        status.code === KnmErknmStatusCodesEnum.FILL_IN_PROCESS ||
        status.code === KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT)
    );
  }, [knmSystemType, pmType, status]);

  return (
    <FieldBlock
      label={title}
      id={blockId}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          id="erknmInspectorsAddButton"
          disabled={watchOnly}
          onClick={addInspector}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = getIn(props.errors || {}, 'inspectorsErknm');
          const isTouched = props.touched?.inspectorsErknm;

          if (isTouched && fieldError && typeof fieldError === 'string') {
            return <p className={`shared-input-error`}>{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      {shouldShowAdviceFromPm && (
        <HadToBeHowInSignMessage message={isValid4121Cert ? 'Одно из ФИО должно совпадать со сведениями из ЭЦП' : ''} />
      )}
      <FieldArray name={name}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormInspector, HTMLElement>) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          if (!fields.length) {
            return null;
          }

          return (
            <>
              <Row>
                {visibleItems.map((fieldValue: IKnmErknmFormInspector) => {
                  const sIndex =
                    fields.value?.findIndex((item: IKnmErknmFormInspector) => item.listId === fieldValue.listId) ?? 0;
                  const inspectorFieldName = `${name}[${sIndex}]`;
                  const id = getItemBlockId(fieldValue.listId);
                  const lateFieldName = `/inspectors/${fieldValue.guid}`;

                  const isAvailablePosition = !!positionsSelectOptions?.find(i => i.id === fieldValue.position?.id);

                  return (
                    <Row key={id} id={id} margin="medium" isListItem notWrap>
                      <FieldBlock size="half" id={`${id}.fullName`}>
                        <TextInputField
                          className={styles.FullNameField}
                          name={`${inspectorFieldName}.fullName`}
                          placeholder={getTitleFieldWithRedStar({
                            label: 'Введите ФИО должностного лица',
                            field: `${inspectorFieldName}.fullName`,
                            isPm: knmSystemType === KnmSystemTypesEnum.PM,
                            isKnm: knmSystemType === KnmSystemTypesEnum.ERKNM,
                          })}
                          disabled={watchOnly}
                        />
                      </FieldBlock>
                      <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/fullName`} fieldId={`${id}.fullName`} />
                      <FieldBlock size="half" id={`${id}.position`}>
                        <div className="shared-field-row">
                          <SelectInputField
                            className={styles.PositionField}
                            name={`${inspectorFieldName}.position`}
                            id={`${inspectorFieldName}.position`}
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Выберите должность',
                              field: `${inspectorFieldName}.position`,
                              isPm: knmSystemType === KnmSystemTypesEnum.PM,
                              isKnm: knmSystemType === KnmSystemTypesEnum.ERKNM,
                            })}
                            options={positionsSelectOptions}
                            isSearchable
                            disabled={
                              // eslint-disable-next-line no-nested-ternary
                              (fieldValue.position ? (isAvailablePosition ? watchOnly : false) : watchOnly) ||
                              isWatchOnlyByTestUser
                            }
                            size="medium"
                          />
                          <KnmLateFieldsRemark
                            lateFieldPath={`${lateFieldName}/position/title`}
                            fieldId={`${id}.position`}
                            isLeftPosition={true}
                          />
                          {(!fieldValue.guid || !isDisableDelete) && (
                            <CloseButton
                              className="shared-deleting-row-btn"
                              disabled={fieldValue.guid ? isDisableDelete : false}
                              onClick={makeRemoveInspectorHandler(sIndex)}
                            />
                          )}
                          <Tooltip
                            placement="left"
                            type="history"
                            content={
                              <div className="shared-tooltip-info-content">
                                Отсутствует связь с КНО и видом контроля в ЕРВК
                              </div>
                            }
                          >
                            <ErvkCorrectionRequiredInfo checkForInspectors />
                          </Tooltip>
                        </div>
                      </FieldBlock>
                    </Row>
                  );
                })}
              </Row>
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmInspectorsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmSystemType: getKnmSystemType(state),
  status: getKnmStatus(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
});

export const ErknmInspectors = connect(mapStateToProps)(ErknmInspectorsWithoutStore);
